/**
 * Asking a line to run on one pull request (ADR-0044 spec edge case 11 and 12).
 *
 * A clock or queue line runs on demand by starting its own next run: the
 * backend's `POST /api/desks/:id/jobs/:jobId/run` is the whole story. An EVENT
 * line cannot, because a run with no event has no subject; edge case 11 allows
 * it "only when the ask NAMES the event's subject", which for the pull-request
 * kinds is the shape the MCP and the retired web forms always took.
 *
 * This is what keeps edge case 12 true. An Engagement set to "when asked" is
 * asked, never automatic, and Fletcher's beta population is mostly on-demand:
 * after the engagement pages retired there was briefly nowhere to ask from at
 * all, which made those Engagements unusable rather than manual.
 *
 * **Nothing here keys off a kind id.** Which lines can be asked comes from the
 * catalog's `listens` (`jobSubjectIsPullRequest`), and which endpoint serves
 * the ask comes from the line's ACT, which is the closed list in
 * `packages/shared`. A kind added to the catalog with a pull-request trigger
 * and one of these acts gets the picker with no change here.
 */

import type { JobActId, JobKindWire } from "@yagni/shared";
import { jobSubjectIsPullRequest } from "@yagni/shared";

/** How one act asks for a run on a named pull request. */
interface PullRequestAsk {
  /** The verb on the button, in a manager's words. */
  label: string;
  /** What the person types: a number, or a number or URL. */
  accepts: "number" | "number-or-url";
  send: (input: {
    deskId: string;
    /** `owner/name`, when the line names exactly one repository. */
    repo: string | null;
    prNumber: number;
    /** What the person typed, verbatim, for the endpoints that parse it. */
    raw: string;
  }) => Promise<Response>;
}

function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * The asks, by act. Two today, and both are the endpoint the deleted form
 * posted to, so the case lands in Work exactly where it always did.
 */
const ASK_BY_ACT: Partial<Record<JobActId, PullRequestAsk>> = {
  // Fletcher verifying a pull request. `startWorkerRequest(…, "fletcher", …)`
  // behind `actions/test-pr`, the same call `QaRequestForm` made.
  publish_verdict: {
    label: "Verify a pull request",
    accepts: "number",
    send: ({ deskId, repo, prNumber }) =>
      post("/api/worker-requests/actions/test-pr", {
        teamId: deskId,
        repo,
        prNumber,
        // The endpoint dedupes on this, so a double press is one request.
        operationKey: crypto.randomUUID(),
      }),
  },
  // Proctor reviewing one. The review-requests endpoint parses a number or a
  // pull request URL itself, so the raw input goes through untouched.
  approve_pr: {
    label: "Review a pull request",
    accepts: "number-or-url",
    send: ({ deskId, repo, raw }) =>
      post(`/api/code-review/desks/${encodeURIComponent(deskId)}/review-requests`, {
        input: raw,
        ...(repo ? { repo } : {}),
      }),
  },
};

/**
 * The ask for this line, or null when it has none.
 *
 * Both halves have to hold: the line's run must be ABOUT a pull request, and
 * its act must have somewhere to send the ask. A merge line passes the first
 * test and fails the second, which is the right answer: merge is at the
 * permanent floor and a person merges on the case file, not from a settings
 * page.
 */
export function pullRequestAskFor(kind: JobKindWire): PullRequestAsk | null {
  if (!jobSubjectIsPullRequest(kind)) return null;
  if (!kind.act) return null;
  return ASK_BY_ACT[kind.act] ?? null;
}

/** The repositories this line covers, for validating what a person types. */
export function lineRepos(filter: unknown, teamRepos: readonly string[]): string[] {
  const named = (filter as { repos?: unknown } | null)?.repos;
  if (Array.isArray(named)) {
    const list = named.filter((entry): entry is string => typeof entry === "string");
    if (list.length > 0) return list;
  }
  // A line that names no repository covers everything the Team attaches.
  return [...teamRepos];
}

/** What a person typed, as a pull request number. Null when it is not one. */
export function parsePrNumber(raw: string): number | null {
  const text = raw.trim();
  if (text === "") return null;
  const plain = /^#?(\d+)$/.exec(text);
  if (plain) {
    const value = Number(plain[1]);
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  // A pasted GitHub URL: .../owner/name/pull/123 (with anything after it).
  const url = /\/pull\/(\d+)(?:[/?#]|$)/.exec(text);
  if (url) {
    const value = Number(url[1]);
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  return null;
}

/** `owner/name` out of a pasted pull request URL, when it carries one. */
export function parseRepoFromUrl(raw: string): string | null {
  const match = /github\.com\/([^/\s]+\/[^/\s]+)\/pull\/\d+/.exec(raw.trim());
  return match ? match[1]! : null;
}
